import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  Tabs,
  Tab,
  Chip,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Person,
  Edit,
  Save,
  Cancel,
  Logout,
  CalendarToday,
  AttachMoney
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { bookingService, customTourRequestService } from '../services/firebase';
import { Booking, CustomTourRequest } from '../types';
import { useTranslation } from 'react-i18next';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile, signOut, updateUserProfile } = useAuth();
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [customTourRequests, setCustomTourRequests] = useState<CustomTourRequest[]>([]);
  const [customTourLoading, setCustomTourLoading] = useState(true);

  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    phoneNumber: '',
    bio: ''
  });

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (userProfile) {
      setFormData({
        displayName: userProfile.displayName || '',
        email: userProfile.email || '',
        phoneNumber: userProfile.phoneNumber || '',
        bio: userProfile.bio || ''
      });
    }

    fetchBookings();
    fetchCustomTourRequests();
  }, [currentUser, userProfile, navigate]);

  const fetchBookings = async () => {
    if (!currentUser) return;

    try {
      setBookingsLoading(true);
      const userBookings = await bookingService.getUserBookings(currentUser.uid);
      // Sadece kullanıcının kendi rezervasyonlarını göster (misafir rezervasyonları hariç)
      const userOwnBookings = userBookings.filter(booking => !booking.isGuestBooking);
      setBookings(userOwnBookings);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(t('profilePage.errors.bookings'));
    } finally {
      setBookingsLoading(false);
    }
  };

  const fetchCustomTourRequests = async () => {
    if (!currentUser) return;
    try {
      setCustomTourLoading(true);
      const allRequests = await customTourRequestService.getCustomTourRequests();
      const userRequests = allRequests.filter((r: CustomTourRequest) => r.email === currentUser.email || r.phone === currentUser.phoneNumber);
      setCustomTourRequests(userRequests);
    } catch (err) {
      setError(t('profilePage.errors.customTours'));
    } finally {
      setCustomTourLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEdit = () => {
    setEditMode(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setEditMode(false);
    if (userProfile) {
      setFormData({
        displayName: userProfile.displayName || '',
        email: userProfile.email || '',
        phoneNumber: userProfile.phoneNumber || '',
        bio: userProfile.bio || ''
      });
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      console.log('Saving profile data:', formData);
      
      await updateUserProfile({
        displayName: formData.displayName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        bio: formData.bio
      });
      
      setEditMode(false);
      setSuccess(t('profilePage.success.profileUpdated'));
    } catch (err) {
      console.error('Error updating profile:', err);
      if (err instanceof Error) {
        setError(`${t('profilePage.errors.profileUpdateError')}: ${err.message}`);
      } else {
        setError(t('profilePage.errors.profileUpdateError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (err) {
      console.error('Error signing out:', err);
      setError(t('profilePage.errors.signOutError'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      case 'completed': return 'info';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    return t(`bookingStatus.${status}`, status);
  };

  const getRequestStatusText = (status: string) => {
    return t(`requestStatus.${status}`, status);
  };

  if (!currentUser) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh',
          backgroundColor: 'var(--light-gray-white)'
        }}>
          <CircularProgress sx={{ color: 'var(--light-blue-cta)' }} />
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('profilePage.helmetTitle')} - HatTravel</title>
        <meta name="description" content={t('profilePage.helmetDescription')} />
      </Helmet>
      
      <Box sx={{ 
        backgroundColor: 'var(--light-gray-white)',
        minHeight: '100vh'
      }}>
        {/* Hero Section */}
        <Box sx={{ 
          backgroundColor: 'var(--light-gray-white)',
          py: 6,
          mb: 4
        }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  mx: 'auto',
                  mb: 3,
                  backgroundColor: 'var(--light-blue-cta)',
                  fontSize: '3rem',
                  boxShadow: '0 8px 24px rgba(0,91,189,0.2)'
                }}
              >
                <Person sx={{ fontSize: 60 }} />
              </Avatar>
              
              <Typography 
                variant="h2" 
                component="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  color: 'var(--navy-blue)',
                  mb: 2
                }}
              >
                {userProfile?.displayName || currentUser.email}
              </Typography>
              
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                {t('profilePage.subtitle')}
              </Typography>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg">
          {/* Error/Success Messages */}
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
              {success}
            </Alert>
          )}

          {/* Tabs */}
          <Card sx={{ 
            mb: 4,
            backgroundColor: 'white',
            borderRadius: 4,
            boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
            overflow: 'hidden'
          }}>
            <Box sx={{ borderBottom: '1px solid var(--gray-shadow)' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': {
                    color: 'text.secondary',
                    fontWeight: 500,
                    '&.Mui-selected': {
                      color: 'var(--light-blue-cta)',
                      fontWeight: 600
                    }
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'var(--light-blue-cta)'
                  }
                }}
              >
                <Tab label={t('profilePage.tabs.profile')} />
                <Tab label={t('profilePage.tabs.bookings')} />
                <Tab label={t('profilePage.tabs.customTours')} />
              </Tabs>
            </Box>

            {/* Profile Info Tab */}
            <TabPanel value={tabValue} index={0}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'var(--navy-blue)' }}>
                    {t('profilePage.profile.title')}
                  </Typography>
                  {!editMode ? (
                    <Button
                      startIcon={<Edit />}
                      onClick={handleEdit}
                      sx={{
                        backgroundColor: 'var(--light-blue-cta)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'var(--cobalt-blue)'
                        }
                      }}
                    >
                      {t('profilePage.profile.edit')}
                    </Button>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        startIcon={<Save />}
                        onClick={handleSave}
                        disabled={loading}
                        sx={{
                          backgroundColor: 'var(--light-blue-cta)',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'var(--cobalt-blue)'
                          }
                        }}
                      >
                        {loading ? <CircularProgress size={20} /> : t('profilePage.profile.save')}
                      </Button>
                      <Button
                        startIcon={<Cancel />}
                        onClick={handleCancel}
                        sx={{
                          borderColor: 'var(--gray-shadow)',
                          color: 'text.secondary',
                          '&:hover': {
                            borderColor: 'var(--cobalt-blue)',
                            backgroundColor: 'var(--light-gray-white)'
                          }
                        }}
                        variant="outlined"
                      >
                        {t('profilePage.profile.cancel')}
                      </Button>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                  <TextField
                    label={t('profilePage.profile.displayName')}
                    fullWidth
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    disabled={!editMode}
                    sx={{
                      gridColumn: { sm: 'span 8' },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: editMode ? 'white' : 'var(--light-gray-white)'
                      }
                    }}
                  />
                  <TextField
                    label={t('profilePage.profile.email')}
                    fullWidth
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    disabled={!editMode}
                    sx={{
                      gridColumn: { sm: 'span 4' },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: editMode ? 'white' : 'var(--light-gray-white)'
                      }
                    }}
                  />
                  <TextField
                    label={t('profilePage.profile.phone')}
                    fullWidth
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    disabled={!editMode}
                    sx={{
                      gridColumn: { sm: 'span 4' },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: editMode ? 'white' : 'var(--light-gray-white)'
                      }
                    }}
                  />
                  <TextField
                    label={t('profilePage.profile.bio')}
                    multiline
                    rows={3}
                    fullWidth
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    disabled={!editMode}
                    sx={{
                      gridColumn: { sm: 'span 8' },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: editMode ? 'white' : 'var(--light-gray-white)'
                      }
                    }}
                  />
                </Box>

                <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid var(--gray-shadow)' }}>
                  <Button
                    startIcon={<Logout />}
                    onClick={handleSignOut}
                    color="error"
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      borderColor: '#d32f2f',
                      color: '#d32f2f',
                      '&:hover': {
                        borderColor: '#d32f2f',
                        backgroundColor: 'rgba(211, 47, 47, 0.04)'
                      }
                    }}
                  >
                    {t('profilePage.signOut')}
                  </Button>
                </Box>
              </CardContent>
            </TabPanel>

            {/* Booking History Tab */}
            <TabPanel value={tabValue} index={1}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'var(--navy-blue)', mb: 4 }}>
                  {t('profilePage.bookings.title')}
                </Typography>

                {bookingsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: 'var(--light-blue-cta)' }} />
                  </Box>
                ) : bookings.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <CalendarToday sx={{ fontSize: 80, color: 'var(--gray-shadow)', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                      {t('profilePage.bookings.noBookings')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      İlk rezervasyonunuzu yapmak için turlarımıza göz atın.
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                    {bookings.map((booking) => (
                      <ListItem
                        key={booking.id}
                        sx={{
                          mb: 2,
                          p: 3,
                          backgroundColor: 'white',
                          borderRadius: 3,
                          border: '1px solid var(--gray-shadow)',
                          '&:hover': {
                            backgroundColor: 'var(--light-gray-white)'
                          }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--navy-blue)' }}>
                                {booking.tourName}
                              </Typography>
                              <Chip
                                label={getStatusText(booking.status)}
                                color={getStatusColor(booking.status) as any}
                                size="small"
                                sx={{ fontWeight: 500 }}
                              />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarToday sx={{ fontSize: 16, color: 'var(--light-blue-cta)' }} />
                                <Typography variant="body2" color="text.secondary">
                                  {booking.tourDate instanceof Date 
                                    ? booking.tourDate.toLocaleDateString('tr-TR')
                                    : booking.tourDate?.toDate?.()?.toLocaleDateString('tr-TR') || 'Tarih belirtilmemiş'
                                  }
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AttachMoney sx={{ fontSize: 16, color: 'var(--light-blue-cta)' }} />
                                <Typography variant="body2" color="text.secondary">
                                  ₺{booking.totalPrice?.toLocaleString() || '0'}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </TabPanel>

            {/* Custom Tour Requests Tab */}
            <TabPanel value={tabValue} index={2}>
              <Typography variant="h6" mb={2}>{t('profilePage.customTours.title')}</Typography>
              {customTourLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : customTourRequests.length === 0 ? (
                <Alert severity="info">{t('profilePage.customTours.noRequests')}</Alert>
              ) : (
                <List>
                  {customTourRequests.map((req) => (
                    <ListItem key={req.id} alignItems="flex-start" sx={{ mb: 2, border: '1px solid #eee', borderRadius: 2 }}>
                      <ListItemText
                        primary={<>
                          <Typography variant="subtitle1" fontWeight={600}>{req.tourType}</Typography>
                          <Typography variant="body2" color="text.secondary">{req.destination} | {req.preferredDates}</Typography>
                        </>}
                        secondary={<>
                          <Typography variant="body2" color="text.secondary">Durum: <b>{req.status}</b></Typography>
                          <Typography variant="body2" color="text.secondary">Talep Tarihi: {req.createdAt?.toDate?.().toLocaleDateString?.('tr-TR') || ''}</Typography>
                          <Typography variant="body2" color="text.secondary">Mesaj: {req.message}</Typography>
                        </>}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </TabPanel>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default ProfilePage; 